import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Reveal } from '@/components/generals/reveal'
import { ScaleDivider } from '@/components/generals/hairline'
import { Orrery } from '@/components/orrery/orrery'
import { MISSIONS, SOLAR_BODIES } from '@/constants/solar-system'

const SolarSystemPage: FC = (): ReactNode => {
    const planets = SOLAR_BODIES.filter(body => body.kind === 'Planet')

    return (
        <Layout>
            <Seo
                title='Solar System'
                description='A live 3D orrery of the Solar System with every planet in its real position, true and readable scale, planet and moon facts, and the paths of Voyager, Pioneer and New Horizons.'
            />

            <Orrery variant='page' />

            <section aria-labelledby='scale-title' className='container-page grid gap-12 py-28 lg:grid-cols-12'>
                <div className='lg:col-span-5'>
                    <p className='label-mono text-brass'>
                        Why two scales
                    </p>
                    <h2 id='scale-title' className='display-serif mt-4 text-5xl text-star'>
                        Mostly, it is empty
                    </h2>
                </div>
                <Reveal variant='wipe' className='space-y-5 text-star-2 lg:col-span-7'>
                    <p>
                        Every picture of the Solar System you have seen squeezes the distances so that the planets fit on a page. The readable scale does the same, so you can see where everything is.
                    </p>
                    <p>
                        Switch to true scale and the planets shrink to specks you cannot see: if the Sun were a grapefruit in the dome, the Earth would be a grain of salt eleven metres away and Neptune a speck of dust more than three hundred metres down the hill. The labels mark where each world really is.
                    </p>
                </Reveal>
            </section>

            <div className='container-page'>
                <ScaleDivider />
            </div>

            <section aria-labelledby='planets-title' className='container-page py-24'>
                <h2 id='planets-title' className='label-mono text-star-3'>
                    The eight planets
                </h2>
                <div className='mt-8 grid gap-px bg-hair sm:grid-cols-2 lg:grid-cols-4'>
                    {planets.map(planet => (
                        <article key={planet.key} className='bg-ink p-6'>
                            <h3 className='font-heading text-3xl text-star'>
                                {planet.name}
                            </h3>
                            <p className='mt-3 text-sm leading-relaxed text-star-2'>
                                {planet.summary}
                            </p>
                            <dl className='data-mono mt-5 space-y-1.5 text-[0.75rem] text-star-3'>
                                <div className='flex justify-between gap-3'>
                                    <dt>Diameter</dt>
                                    <dd className='text-star-2'>{planet.diameter}</dd>
                                </div>
                                <div className='flex justify-between gap-3'>
                                    <dt>Year</dt>
                                    <dd className='text-right text-star-2'>{planet.year}</dd>
                                </div>
                                <div className='flex justify-between gap-3'>
                                    <dt>Moons</dt>
                                    <dd className='text-star-2'>{planet.moons}</dd>
                                </div>
                            </dl>
                        </article>
                    ))}
                </div>
            </section>

            <section aria-labelledby='missions-title' className='container-page pb-32'>
                <p className='label-mono text-brass'>
                    Mission paths
                </p>
                <h2 id='missions-title' className='display-serif mt-4 text-5xl text-star'>
                    Five ships leaving home
                </h2>
                <p className='mt-4 max-w-2xl text-star-2'>
                    Turn on mission paths in the orrery to watch them fly. Their routes are drawn through the real positions of the planets on the days they flew past, and their distances update as time runs.
                </p>
                <ul className='mt-10'>
                    {MISSIONS.map(mission => (
                        <li key={mission.key} className='grid gap-2 border-b border-hair py-5 md:grid-cols-[14rem_10rem_1fr] md:gap-8'>
                            <span className='font-heading text-2xl text-star'>
                                {mission.name}
                            </span>
                            <span className='data-mono text-star-3'>
                                {`Launched ${mission.launch.slice(0, 4)}`}
                            </span>
                            <span className='text-[0.95rem] text-star-2'>
                                {mission.status}
                            </span>
                        </li>
                    ))}
                </ul>
            </section>
        </Layout>
    )
}

export default SolarSystemPage
